import {
  forwardRef,
  type ReactNode,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  cloneElement,
  isValidElement,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility';
import { calculateTooltipPosition } from './tooltip/tooltipPosition';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Tooltip placement
 */
type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

/**
 * Props for the Tooltip component
 */
export interface TooltipProps {
  /** Tooltip content */
  content: ReactNode;
  /** Trigger element */
  children: ReactNode;
  /** Placement of the tooltip */
  placement?: TooltipPlacement;
  /** Delay before showing tooltip (ms) */
  delay?: number;
  /** Delay before hiding tooltip (ms) */
  hideDelay?: number;
  /** Disable the tooltip */
  disabled?: boolean;
  /** Show arrow */
  arrow?: boolean;
  /** Additional className for tooltip */
  className?: string;
  /** Offset from the trigger element */
  offset?: number;
}

/**
 * Animation variants
 */
const getAnimationVariants = (placement: TooltipPlacement) => {
  const offsets = {
    top: { x: 0, y: 8 },
    bottom: { x: 0, y: -8 },
    left: { x: 8, y: 0 },
    right: { x: -8, y: 0 },
  };

  return {
    hidden: {
      opacity: 0,
      scale: 0.95,
      ...offsets[placement],
    },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 400,
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      ...offsets[placement],
      transition: { duration: 0.15 },
    },
  };
};

/**
 * Arrow styles based on placement
 */
const arrowStyles: Record<TooltipPlacement, string> = {
  top: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-t-white/[0.12] border-x-transparent border-b-transparent',
  bottom: 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-b-white/[0.12] border-x-transparent border-t-transparent',
  left: 'right-0 top-1/2 -translate-y-1/2 translate-x-full border-l-white/[0.12] border-y-transparent border-r-transparent',
  right: 'left-0 top-1/2 -translate-y-1/2 -translate-x-full border-r-white/[0.12] border-y-transparent border-l-transparent',
};

/**
 * Tooltip component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Tooltip content="This is a tooltip" placement="top">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * <Tooltip
 *   content={
 *     <div>
 *       <strong>Rich Content</strong>
 *       <p>Tooltips can contain any content</p>
 *     </div>
 *   }
 *   placement="right"
 *   arrow
 * >
 *   <span>Hover for info</span>
 * </Tooltip>
 * ```
 */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      content,
      children,
      placement = 'top',
      delay = 200,
      hideDelay = 0,
      disabled = false,
      arrow = true,
      className,
      offset = 8,
    },
    _ref
  ) => {
    const { isVisible, show: handleShow, hide: handleHide } = useDelayedVisibility({
      delay,
      hideDelay,
      disabled,
    });
    // Der ausloesende DOM-Knoten wird beim Ueberfahren/Fokussieren aus dem
    // Event uebernommen (siehe unten) statt ueber einen aufgesetzten Ref. So
    // bleibt der eigene Ref des Kindes unangetastet.
    const [triggerNode, setTriggerNode] = useState<HTMLElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Calculate position
    //
    // Die berechnete Position wird direkt auf den Tooltip-Knoten geschrieben
    // statt in React-State gehalten: Sie haengt an einer Messung des schon
    // eingehaengten DOM (Groesse des Tooltips, Rechteck des Triggers) und ist
    // damit eine Synchronisation mit einem externen System, kein Zustand, aus
    // dem gerendert wird. Ein Umweg ueber State wuerde nach jedem Scroll- und
    // Resize-Ereignis einen zusaetzlichen Render ausloesen.
    const updatePosition = useCallback(() => {
      const tooltipNode = tooltipRef.current;
      if (!triggerNode || !tooltipNode) return;

      const triggerRect = triggerNode.getBoundingClientRect();
      const tooltipRect = tooltipNode.getBoundingClientRect();

      // Rechnen passiert in einer reinen, getesteten Funktion; hier nur messen
      // und das Ergebnis auf den Knoten schreiben.
      const { top, left } = calculateTooltipPosition(
        triggerRect,
        { width: tooltipRect.width, height: tooltipRect.height },
        placement,
        offset,
        {
          width: window.innerWidth,
          height: window.innerHeight,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        }
      );

      tooltipNode.style.top = `${top}px`;
      tooltipNode.style.left = `${left}px`;
    }, [placement, offset, triggerNode]);

    // Update position when visible
    // Layout-Effekt, weil die Position erst nach dem Einhaengen messbar ist
    // (Groesse des Tooltips) und vor dem Zeichnen stehen muss.
    useLayoutEffect(() => {
      if (isVisible) {
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
          window.removeEventListener('scroll', updatePosition, true);
          window.removeEventListener('resize', updatePosition);
        };
      }
    }, [isVisible, updatePosition]);

    // Clone children with event handlers
    interface TriggerChildProps {
      onMouseEnter?: (e: React.MouseEvent) => void;
      onMouseLeave?: (e: React.MouseEvent) => void;
      onFocus?: (e: React.FocusEvent) => void;
      onBlur?: (e: React.FocusEvent) => void;
    }

    // `currentTarget` ist immer das Element, an dem der Handler haengt — also
    // genau der Trigger, dessen Rechteck die Positionsberechnung braucht.
    // Frueher wurde dafuer ein Ref auf das Kind gesetzt und dessen eigener Ref
    // von Hand nachgezogen; das mutierte ein fremdes, aus den Props stammendes
    // Ref-Objekt.
    const trigger = isValidElement<TriggerChildProps>(children)
      ? cloneElement(children, {
          onMouseEnter: (e: React.MouseEvent) => {
            setTriggerNode(e.currentTarget as HTMLElement);
            handleShow();
            children.props.onMouseEnter?.(e);
          },
          onMouseLeave: (e: React.MouseEvent) => {
            handleHide();
            children.props.onMouseLeave?.(e);
          },
          onFocus: (e: React.FocusEvent) => {
            setTriggerNode(e.currentTarget as HTMLElement);
            handleShow();
            children.props.onFocus?.(e);
          },
          onBlur: (e: React.FocusEvent) => {
            handleHide();
            children.props.onBlur?.(e);
          },
        })
      : children;

    const tooltipContent = (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={tooltipRef}
            variants={getAnimationVariants(placement)}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="tooltip"
            className={cn(
              'fixed z-[100] max-w-xs px-3 py-2',
              'backdrop-blur-xl',
              'bg-surface-100/95',
              'border border-white/[0.12]',
              'rounded-lg',
              'shadow-xl shadow-black/40',
              'text-sm text-surface-800',
              'pointer-events-none',
              className
            )}
          >
            {content}

            {/* Arrow */}
            {arrow && (
              <span
                className={cn(
                  'absolute w-0 h-0',
                  'border-[6px]',
                  arrowStyles[placement]
                )}
                aria-hidden="true"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    );

    return (
      <>
        {trigger}
        {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
      </>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
